import { useState, useEffect, type CSSProperties } from 'react';
import { useUIStore } from '@/state/store';
import { apiFetch, getDisplayName } from '@/lib/auth';

const ctaStyle: CSSProperties = {
  width: '100%', marginTop: 12, padding: '12px 16px', fontSize: 15, fontWeight: 600,
  border: 'none', borderRadius: 10, cursor: 'pointer', background: '#2bb6a3', color: '#042',
};
const linkBoxStyle: CSSProperties = {
  fontSize: 12.5, wordBreak: 'break-all', padding: '10px 12px', borderRadius: 8,
  background: 'rgba(0,0,0,0.25)', border: '1px solid rgba(255,255,255,0.12)', fontFamily: 'monospace',
};

/** Welle 8d: echter Magic-Link-Flow für das laufende Gespräch (ad-hoc). */
function EscalateContent() {
  const close = useUIStore((s) => s.closeDrawers);
  const [phase, setPhase] = useState<'idle' | 'creating' | 'done' | 'error'>('idle');
  const [url, setUrl] = useState('');
  const [err, setErr] = useState('');
  const [copied, setCopied] = useState(false);

  const createLink = async () => {
    const callId = (useUIStore.getState() as any).activeCallId ?? (window as any).__activeCallId ?? null;
    if (!callId) {
      setErr('Kein aktives Gespräch gefunden.');
      setPhase('error');
      return;
    }
    setPhase('creating');
    setErr('');
    try {
      const name = getDisplayName();
      const res = await apiFetch('/api/links', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          kind: 'call',
          callId,
          targetLabel: name ? `Gespräch mit ${name}` : 'laufendes Gespräch',
        }),
      });
      if (!res.ok) {
        const b = await res.json().catch(() => ({}));
        setErr(b?.message || 'Link konnte nicht erzeugt werden.');
        setPhase('error');
        return;
      }
      const data = await res.json();
      setUrl(data.url);
      setPhase('done');
    } catch {
      setErr('Verbindung fehlgeschlagen.');
      setPhase('error');
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      /* Clipboard nicht verfügbar — Link steht sichtbar zum manuellen Kopieren. */
    }
  };

  const sendMail = () => {
    const subject = encodeURIComponent('Einladung zum Videogespräch · Bamoa Videoservice');
    const body = encodeURIComponent(
      `Guten Tag,\n\nbitte treten Sie über den folgenden Link unserem laufenden Videogespräch bei:\n\n${url}\n\n` +
        `Sie werden nach Ihrem Namen gefragt; der Beitritt wird anschließend von mir bestätigt. ` +
        `Eine Teilnahme ist auch per Smartphone oder Tablet im Browser möglich – ohne App.\n\nMit freundlichen Grüßen`,
    );
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };

  return (
    <>
      <div className="drawer-head">
        <div className="drawer-title">Experten zuschalten · Magic-Link</div>
        <button className="drawer-close" onClick={close}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M18 6L6 18M6 6l12 12" />
          </svg>
        </button>
      </div>

      {phase !== 'done' && (
        <div style={{ padding: '4px 2px 2px', fontSize: 13.5, color: 'var(--muted, #8aa)', lineHeight: 1.5 }}>
          Erzeugt einen einmaligen Einladungslink zu <strong>diesem</strong> Gespräch. Der Experte gibt
          seinen Namen ein und du bestätigst den Beitritt.
        </div>
      )}

      {phase !== 'done' && (
        <button className="drawer-cta" disabled={phase === 'creating'} onClick={createLink} style={ctaStyle}>
          {phase === 'creating' ? 'Wird erzeugt…' : 'Magic-Link erzeugen'}
        </button>
      )}

      {phase === 'error' && <div style={{ color: '#d8785a', fontSize: 13, marginTop: 8 }}>{err}</div>}

      {phase === 'done' && (
        <div style={{ marginTop: 6 }}>
          <div style={{ fontSize: 12.5, color: 'var(--muted, #8aa)', marginBottom: 6 }}>
            Einladungslink:
          </div>
          <div style={linkBoxStyle}>{url}</div>
          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            <button
              className="drawer-cta"
              onClick={copy}
              style={{ ...ctaStyle, flex: 1, marginTop: 0, background: 'rgba(255,255,255,0.10)', color: 'inherit' }}
            >
              {copied ? '✓ Kopiert' : 'Kopieren'}
            </button>
            <button className="drawer-cta" onClick={sendMail} style={{ ...ctaStyle, flex: 1, marginTop: 0 }}>
              Per E-Mail senden
            </button>
          </div>
        </div>
      )}

      <div className="drawer-footer">
        <span>Bleibst im Call · 3-Way-Mesh</span>
      </div>
    </>
  );
}

export function EscalateDrawer() {
  const open = useUIStore((s) => s.escalateOpen);
  const close = useUIStore((s) => s.closeDrawers);
  return (
    <>
      <div className={`drawer-backdrop ${open ? 'active' : ''}`} onClick={close} />
      <div
        className={`drawer ${open ? 'active' : ''}`}
        style={{ left: '50%', bottom: 100, transform: open ? 'translateX(-50%)' : 'translateX(-50%) translateY(20px) scale(0.96)' }}
      >
        <EscalateContent />
      </div>
    </>
  );
}

export function TransferDrawer() {
  const open = useUIStore((s) => s.transferOpen);
  const close = useUIStore((s) => s.closeDrawers);
  return (
    <>
      <div className={`drawer-backdrop ${open ? 'active' : ''}`} onClick={close} />
      <div
        className={`drawer ${open ? 'active' : ''}`}
        style={{ left: '50%', bottom: 100, transform: open ? 'translateX(-50%)' : 'translateX(-50%) translateY(20px) scale(0.96)' }}
      >
        {open && <TransferBody />}
      </div>
    </>
  );
}

interface RosterAdviser { id: string; displayName: string; status: string | null; sub: string }

/** Welle 9: echte Weiterleitung — Kollege wählen → Blind-Transfer anbieten. */
function TransferBody() {
  const close = useUIStore((s) => s.closeDrawers);
  const [people, setPeople] = useState<RosterAdviser[]>([]);
  const [q, setQ] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const [msg, setMsg] = useState('');
  const myId = typeof localStorage !== 'undefined' ? localStorage.getItem('bamoa_user_id') : null;

  useEffect(() => {
    (async () => {
      try {
        const r = await apiFetch('/api/roster');
        if (!r.ok) return;
        const data = await r.json();
        const seen = new Set<string>();
        const list: RosterAdviser[] = [];
        for (const g of data.groups ?? []) {
          for (const m of g.members ?? []) {
            if (seen.has(m.id)) continue;
            seen.add(m.id);
            list.push({ id: m.id, displayName: m.displayName, status: m.status, sub: g.name });
          }
        }
        for (const a of data.all ?? []) {
          if (seen.has(a.id)) continue;
          seen.add(a.id);
          list.push({ id: a.id, displayName: a.displayName, status: a.status, sub: a.sharedGroup ? '' : 'andere Gruppe' });
        }
        setPeople(list.filter((p) => p.id !== myId));
      } catch { /* ignore */ }
    })();
  }, [myId]);

  const callId = (useUIStore.getState() as any).activeCallId ?? (window as any).__activeCallId ?? null;

  async function transferTo(target: RosterAdviser) {
    if (!callId) { setMsg('Kein aktiver Call.'); return; }
    setBusy(target.id); setMsg('');
    try {
      const r = await apiFetch(`/api/calls/${callId}/transfer`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ targetUserId: target.id }),
      });
      if (r.ok) {
        setMsg(`Weiterleitung an ${target.displayName} angeboten – warte auf Annahme…`);
        setTimeout(close, 1200);
      } else {
        const b = await r.json().catch(() => ({}));
        setMsg(b?.message || 'Weiterleitung fehlgeschlagen.');
      }
    } catch { setMsg('Verbindung fehlgeschlagen.'); }
    finally { setBusy(null); }
  }

  const statusLabel = (s: string | null) =>
    s === 'available' ? 'Verfügbar' : s === 'incall' ? 'Im Call' : s === 'pause' ? 'Pause' : s === 'wrap' ? 'Nachbereitung' : s === 'offline' ? 'Offline' : '—';

  const filtered = people.filter((p) => q.trim() === '' || p.displayName.toLowerCase().includes(q.trim().toLowerCase()));

  return (
    <>
      <div className="drawer-head">
        <div className="drawer-title">Weiterleiten · Call übergeben</div>
        <button className="drawer-close" onClick={close}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6L6 18M6 6l12 12" /></svg>
        </button>
      </div>
      <input type="text" className="drawer-search" placeholder="Berater suchen…" value={q} onChange={(e) => setQ(e.target.value)} />
      {msg && <div style={{ fontSize: 12.5, color: 'var(--tenant-accent, #2bb6a3)', padding: '6px 2px' }}>{msg}</div>}
      <div className="drawer-list">
        {filtered.length === 0 && <div className="drawer-item" style={{ opacity: 0.6 }}><div className="b-name">Keine Berater</div></div>}
        {filtered.map((p) => (
          <div key={p.id} className="drawer-item" style={{ cursor: busy ? 'default' : 'pointer', opacity: busy && busy !== p.id ? 0.5 : 1 }}
            onClick={() => !busy && transferTo(p)}>
            <div className="berater-avatar" style={{ width: 30, height: 30, fontSize: 11 }}>
              {p.displayName.split(/\s+/).map((x) => x[0]).slice(0, 2).join('').toUpperCase()}
            </div>
            <div>
              <div className="b-name">{p.displayName}</div>
              <div className="b-meta">{[p.sub, statusLabel(p.status)].filter(Boolean).join(' · ')}</div>
            </div>
            <div className="b-load">{busy === p.id ? '…' : '→'}</div>
          </div>
        ))}
      </div>
      <div className="drawer-footer">
        <span>Du verlässt den Call · Session-Übergabe</span>
        <span>Ziel muss annehmen</span>
      </div>
    </>
  );
}
